/**
 * Réglages de partie mémorisés PAR JEU et partagés TV ↔ télécommande.
 *
 * Avant (jusqu'au 16/10/2026) : la TV gardait ses réglages en mémoire (perdus au
 * rechargement du jeu, malgré « Mémorisés pour vos prochaines soirées »), le
 * téléphone de l'animateur ne voyait que ce qu'il avait lui-même choisi, et
 * `gameParams` était remis à zéro à chaque changement de jeu depuis le téléphone.
 *
 * Maintenant, pour les jeux de `MEMORIZED_GAMES` :
 *  - le jeu sur la TV annonce ses réglages (message `saveGameParams`, cf
 *    unblind-test `useTvSettingsPublisher`) et l'écran les enregistre ;
 *  - la télécommande les écrit aussi ; « le dernier qui touche gagne » ;
 *  - les deux côtés lisent `screenInfos.anim.gameParamsByGame.<gameID>`, qui survit
 *    au changement de jeu ; `gameParams` reste écrit en parallèle (compatibilité
 *    avec une ancienne version de la console encore ouverte).
 * Les autres jeux gardent exactement le fonctionnement d'avant (`gameParams` seul).
 */

/** Jeux dont la TV publie ses réglages. Ajouter un jeu ici = son jeu doit publier aussi. */
export const MEMORIZED_GAMES: ReadonlySet<string> = new Set(['unblind-test']);

export function isMemorizedGame(gameID?: string | null): boolean {
    return !!gameID && MEMORIZED_GAMES.has(gameID);
}

type ScreenParams = {
    gameParams?: Record<string, unknown>;
    gameParamsByGame?: Record<string, Record<string, unknown>>;
};

/**
 * Réglages enregistrés pour ce jeu sur cet écran. Jeu mémorisé : sa mémoire si elle
 * existe, sinon `gameParams` (réglages posés avant cette version). Autre jeu :
 * `gameParams`, comme avant.
 */
export function savedGameParams(screen: ScreenParams | undefined, gameID?: string | null) {
    if (!screen) return undefined;
    if (isMemorizedGame(gameID)) return screen.gameParamsByGame?.[gameID as string] ?? screen.gameParams;
    return screen.gameParams;
}

/** Réglages qui diffèrent entre deux états (`null` = retiré), pour une écriture réglage par réglage. */
export function changedParams(before: Record<string, unknown>, after: Record<string, unknown>) {
    const changes: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) {
        if (!(k in after)) changes[k] = null;
        else if (after[k] !== before[k]) changes[k] = after[k];
    }
    return changes;
}

/** Établissements de démonstration publics (même liste que `isDemo`, firestore.rules) :
    n'importe quel visiteur y écrit, ses réglages ne doivent pas arriver chez les autres. */
export const PUBLIC_DEMO_CLIENTS: ReadonlySet<string> = new Set(['demo', 'airports-demo']);

/** Message `saveGameParams` reçu du jeu (cf unblind-test `TvSettingsMessage`). */
export type TvParamsMessage = {
    gameID?: unknown;
    set?: unknown;
    fill?: unknown;
};

const KEY = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;

/** Ne garde que des réglages plats et sûrs (le jeu est une page tierce dans une iframe). */
function cleanParams(value: unknown, allowedKeys?: ReadonlySet<string>): Record<string, string | number | boolean> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    const out: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        const keyOk = KEY.test(k) && (!allowedKeys || allowedKeys.has(k));
        const valueOk =
            (typeof v === 'string' && v.length <= 200) ||
            (typeof v === 'number' && Number.isFinite(v)) ||
            typeof v === 'boolean';
        if (keyOk && valueOk) out[k] = v as string | number | boolean;
    }
    return out;
}

/**
 * Réglages à enregistrer après un message de la TV, ou `null` s'il n'y a rien à
 * écrire (écho de ce qui est déjà enregistré : pas d'écriture inutile).
 *  - `fill` : ne remplit que les réglages absents, n'écrase JAMAIS un choix ;
 *  - `set`  : réglages touchés sur la TV, ils remplacent les enregistrés.
 * `allowedKeys` = réglages du schéma du jeu (le reste est ignoré).
 */
export function mergeTvParams(
    saved: Record<string, unknown> | undefined,
    message: TvParamsMessage,
    allowedKeys?: ReadonlySet<string>
): Record<string, unknown> | null {
    const base = saved ?? {};
    // Réglages d'avant cette version : `gameParams` peut contenir les clés d'un autre
    // jeu (un seul objet par écran) — on ne range que celles de ce jeu.
    const kept = allowedKeys ? Object.fromEntries(Object.entries(base).filter(([k]) => allowedKeys.has(k))) : base;
    const fill = cleanParams(message.fill, allowedKeys);
    const set = cleanParams(message.set, allowedKeys);
    const next: Record<string, unknown> = { ...fill, ...kept, ...set };
    const changed =
        Object.keys(next).length !== Object.keys(base).length || Object.keys(next).some((k) => next[k] !== base[k]);
    return changed ? next : null;
}
